import posthog, { type CaptureResult, type PostHogConfig } from "posthog-js";

import { redactPath, redactUrl } from "@/lib/analytics/redactUrl";
import { runReplayOnlyOnCleanUrls } from "@/lib/sentry/replayGate";
import { debugLog } from "@/utils/debugLog";

/** The only host allowed to write into the production PostHog project. */
export const PROD_HOST = "app.franklin.ee";

/** Just the bits of the three globals the two gates below read, so a test can pass plain objects. */
type GateEnv = { readonly VITE_APP_ENV?: string };
type GateLocation = { readonly hostname: string };
type GateNavigator = { readonly webdriver?: boolean };

/**
 * Both arms of the replay gate, plus the automation one.
 *
 * `VITE_APP_ENV` is inlined at build time and is set only on the franklin-app PRODUCTION
 * Cloudflare config, so a preview build can never satisfy it; the hostname arm is what stops
 * the production BUNDLE recording when it is served from an alias (`franklin-app.pages.dev`)
 * rather than the real app host. `navigator.webdriver` keeps Playwright out.
 */
export function shouldEnableReplay(
  env: GateEnv = import.meta.env,
  loc: GateLocation = window.location,
  nav: GateNavigator = navigator
): boolean {
  return env.VITE_APP_ENV === "production" && loc.hostname === PROD_HOST && !nav.webdriver;
}

/** A production build being served from somewhere that is not the app host — it must not ingest. */
export function isStrayProdBuild(
  env: GateEnv = import.meta.env,
  loc: GateLocation = window.location
): boolean {
  return env.VITE_APP_ENV === "production" && loc.hostname !== PROD_HOST;
}

/**
 * Event properties PostHog fills with a full URL — fragment included.
 *
 * Matched by shape rather than listed, because the same URL is copied under several
 * prefixes and a missed one leaks just as badly as no redaction at all: alongside
 * `$current_url`/`$referrer` and their `$initial_*` twins, `SessionPropsManager` merges
 * `$session_entry_url`/`$session_entry_referrer` into EVERY event of the session, built
 * from `location.href` — so a session that starts on an auth callback would otherwise
 * carry that fragment for its whole life.
 */
const URL_PROPERTY = /(?:current_url|referrer|entry_url)$/;

/** Event properties PostHog fills with the path of those same URLs (`$session_entry_pathname` included). */
const PATH_PROPERTY = /pathname$/;

/**
 * Strip every credential this app can be handed in a URL before an event leaves the browser
 * (the rules themselves live in `lib/analytics/redactUrl` — Sentry redacts against the same set).
 *
 * PostHog ships all three carriers: `$current_url` (full href) and `$pathname` ride the
 * automatic pageview and every autocaptured event, and their `$session_entry_*` copies ride
 * every event for the rest of the session, so without this a live credential is stored in
 * PostHog. PostHogProvider initialises during render, so the pageview can win the race
 * against supabase-js stripping the hash, and nothing strips the query at all.
 *
 * `before_send` rather than `sanitize_properties`: the latter is deprecated in posthog-js and
 * never sees `$set_once`, which is where the `$initial_*` copies travel.
 *
 * Wrapped whole, like `track` and `withClient`: posthog-js calls its before_send hooks
 * unguarded, and one of those calls is `capture("$snapshot")` from the recorder's own flush
 * timer — a throw there loses the buffer AND the reschedule, so replay would die silently on
 * a frame shape we did not expect. Analytics never breaks what it reports on.
 */
export function redactUrlCredentials(event: CaptureResult | null): CaptureResult | null {
  if (!event) return null;
  try {
    for (const bag of [event.properties, event.$set_once]) {
      if (!bag) continue;
      for (const [key, value] of Object.entries(bag)) {
        if (typeof value !== "string") continue;
        if (URL_PROPERTY.test(key)) bag[key] = redactUrl(value);
        else if (PATH_PROPERTY.test(key)) bag[key] = redactPath(value);
      }
    }
    redactSnapshotHrefs(event.properties);
  } catch {
    // Silent, and the event is DROPPED. The walk redacts in place, so a throw part-way
    // through means half-cleaned properties — sending those would ship whatever credential
    // the walk had not reached yet. One lost event beats one leaked token, and dropping
    // saves the recorder's flush just as well as returning: the hook returns normally, so
    // the buffer and the reschedule survive.
    return null;
  }
  return event;
}

/** The shape of an rrweb frame this cares about — everything else in it is left alone. */
type SnapshotFrame = { data?: { href?: unknown; payload?: { href?: unknown } } };

/**
 * Redact the page URL rrweb writes INSIDE a recording, which the property walk above cannot
 * see: a `$snapshot` carries its frames in `properties.$snapshot_data`, an ARRAY, and the
 * **Meta** frame that opens every full snapshot holds a location taken straight from
 * `window.location.href` (`data.href`), query and fragment included — so `/invite/accept?token=…`,
 * `/share/:token` and an OAuth callback hash all ship whole. That frame is the carrier this
 * closes.
 *
 * The `data.payload.href` branch is belt-and-braces. It covers posthog-js's custom
 * `$url_changed` frame, which this app never emits: that emitter returns early whenever
 * `capture_pageview` is set, and it is set to `"history_change"` below. The branch costs a
 * typeof check and holds if that config ever changes.
 *
 * The replay gate (`lib/sentry/replayGate`) cannot cover this: it re-reads the URL on a
 * 250ms poll, so the frames captured between the navigation and the next tick are already in
 * the buffer when it stops the recorder, and PostHog cannot retract an ingested snapshot.
 * The gate still earns its keep — it is what keeps a credential page's DOM out of the
 * recording — but the credential in the URL is stripped here, on the way out, where the
 * timing cannot matter. (`$pageview` frames are already clean: posthog-js builds them from
 * the captured event's `$current_url`, which this function has redacted by then.)
 */
function redactSnapshotHrefs(properties: CaptureResult["properties"] | undefined): void {
  const frames = properties?.$snapshot_data;
  if (!Array.isArray(frames)) return;
  for (const frame of frames as SnapshotFrame[]) {
    const data = frame?.data;
    if (!data || typeof data !== "object") continue;
    if (typeof data.href === "string") data.href = redactUrl(data.href);
    const payload = data.payload;
    if (payload && typeof payload.href === "string") payload.href = redactUrl(payload.href);
  }
}

/**
 * Where posthog-js persists the first-touch `{r: referrer, u: href}` pair it derives
 * `$initial_current_url` / `$initial_pathname` / `$initial_referrer` from.
 */
const INITIAL_PERSON_INFO_KEY = "$initial_person_info";

/** posthog's own cap on each stored value (`getPersonInfo`), matched so we store what it would. */
const PERSON_INFO_MAX_LENGTH = 1000;

const redactStored = (value: string | undefined) =>
  value === undefined ? undefined : redactUrl(value.substring(0, PERSON_INFO_MAX_LENGTH));

/**
 * Redact the first-touch location posthog persists, which `before_send` cannot reach.
 *
 * `getPersonInfo()` snapshots `location.href` verbatim into localStorage, and the
 * feature-flag request POSTs it straight back out as
 * `person_properties.$initial_current_url` — built from persistence, on a request no
 * capture hook sees. So an invitee whose first ever visit is `/invite/accept?token=…`
 * (or a landing on `/auth/callback#access_token=…` before supabase-js has consumed the
 * hash) ships a live credential with every flag evaluation for as long as that
 * persistence entry lives, however well the captured events are cleaned.
 *
 * Writing the slot ourselves covers both orderings, because posthog only ever fills it
 * with `register_once` (from the first capture): if it is not there yet our redacted
 * value claims it, and if it is, this overwrites it. Called synchronously after `init`,
 * which is ahead of the first flags request — that one is behind a `setTimeout`.
 */
export function redactInitialPersonInfo(client: Pick<typeof posthog, "persistence">): void {
  const persistence = client.persistence;
  if (!persistence) return;

  // Absent means this visit is the one posthog is about to record, so redact what it would
  // store rather than leaving the slot for it to fill raw.
  const info = (persistence.props[INITIAL_PERSON_INFO_KEY] as
    | { r?: string; u?: string }
    | undefined) ?? { r: document.referrer || "$direct", u: window.location.href };

  persistence.register({
    [INITIAL_PERSON_INFO_KEY]: {
      ...info,
      // The referrer carries the same credentials when the landing page was linked from
      // one of our own token-bearing pages; redactUrl returns "$direct" untouched.
      r: redactStored(info.r),
      u: redactStored(info.u),
    },
  });
}

export interface PostHogRuntime {
  /** Whether PostHog was successfully initialized. False when env vars are missing. */
  readonly enabled: boolean;
  /** The shared PostHog instance. Always returned, even in no-op mode — calls are safe. */
  readonly client: typeof posthog;
}

let initialized = false;
let runtime: PostHogRuntime | null = null;

/**
 * Initialize the PostHog singleton once. Safe to call repeatedly.
 *
 * When `VITE_POSTHOG_KEY` is missing, returns a runtime with `enabled: false`
 * and does NOT open any network connections. Hooks built on top still work
 * by returning default flag values.
 */
export function initPostHog(): PostHogRuntime {
  if (runtime) return runtime;

  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  const apiHost = import.meta.env.VITE_POSTHOG_HOST ?? "https://eu.i.posthog.com";

  // A driven browser takes the same path as a missing key: an E2E run must not open a
  // connection, let alone pollute the project with its scripted clicks. A stray production
  // build joins them: `before_send` already refuses to ingest from one, but not initialising
  // at all also keeps its /flags requests — which carry the auth UUID once identified — off
  // the production project. The hostname cannot change without a page load, so deciding this
  // once at init is enough.
  if (!apiKey || navigator.webdriver || isStrayProdBuild()) {
    debugLog(
      "⚠️ PostHog not initialized (VITE_POSTHOG_KEY missing, automation, or stray production build) — feature flags fall back to defaults"
    );
    runtime = { enabled: false, client: posthog };
    return runtime;
  }

  if (!initialized) {
    const config: Partial<PostHogConfig> = {
      api_host: apiHost,
      // Recording starts from `shouldEnableReplay` below, never from init.
      disable_session_recording: true,
      // Identity comes from auth state, not from the events themselves — see PostHogIdentity.
      person_profiles: "identified_only",
      autocapture: true,
      capture_pageview: "history_change",
      // Nothing a user typed or was shown is allowed into a recording or an autocaptured event.
      mask_all_text: true,
      mask_all_element_attributes: true,
      session_recording: { maskAllInputs: true, maskTextSelector: "*" },
      disable_surveys: true,
      // Auth tokens arrive in the URL hash, query and path — never let one reach PostHog.
      // `before_send` covers the captured events; redactInitialPersonInfo below covers the
      // copy PostHog persists and replays on the feature-flag request.
      // The stray-build check sits first: a production bundle served from an alias host must
      // not write into the production project at all, redacted or otherwise. Deliberate
      // defence-in-depth — `initPostHog` refuses the same build above, so this hook is never
      // installed on one; it is what makes the refusal true of the capture path itself
      // rather than only of the init path.
      before_send: (event) => (isStrayProdBuild() ? null : redactUrlCredentials(event)),
      // Respect Do-Not-Track and only persist in localStorage (no cookies).
      persistence: "localStorage",
      respect_dnt: true,
      loaded: (ph) => {
        // ponytail: MODE only ever gates debug logging here — never the key, never replay.
        if (import.meta.env.DEV) ph.debug();
      },
    };
    posthog.init(apiKey, config);
    redactInitialPersonInfo(posthog);
    // The hrefs rrweb writes into `$snapshot_data[]` are redacted in before_send
    // (redactSnapshotHrefs); this gate is what keeps a credential page's DOM — its rendered
    // content, not just its URL — out of the recording in the first place.
    if (shouldEnableReplay()) {
      // `resumable`: stopping is not final here. Unlike Sentry's teardown, a later
      // `startSessionRecording()` re-rolls no sampling decision, so a session that passes
      // through an invite or a share link records again as soon as the URL is clean.
      runReplayOnlyOnCleanUrls(
        () => posthog.startSessionRecording(),
        () => posthog.stopSessionRecording(),
        { resumable: true }
      );
    }
    initialized = true;
    debugLog("✅ PostHog initialized", { apiHost });
  }

  runtime = { enabled: true, client: posthog };
  return runtime;
}

export function getPostHogRuntime(): PostHogRuntime {
  return runtime ?? initPostHog();
}

/**
 * Run something against the live client, or nothing at all when PostHog is disabled.
 * Every failure is swallowed for the same reason `track` swallows its own: analytics is
 * never allowed to break the user action it is reporting on.
 */
function withClient(call: (client: typeof posthog) => void): void {
  try {
    const { enabled, client } = getPostHogRuntime();
    if (enabled) call(client);
  } catch {
    // Deliberately silent.
  }
}

/** Tie this browser to the Supabase auth user. `profile_id` is the numeric `user_profiles` id. */
export function identifyUser(userId: string, props?: { profile_id?: number }): void {
  withClient((client) => client.identify(userId, props));
}

/** The org the user is acting in. No properties: the name is personal-ish data, the id is not. */
export function setOrganisation(orgId: string): void {
  withClient((client) => client.group("organisation", orgId));
}

/**
 * Keys already reported this session, for events that must count a THING rather than a
 * render — one signature per signing session, say. Module state, so it survives a
 * remount and a React Query refetch, and cleared by {@link resetUser} because it is
 * per-person: without that, the second user of a browser gets no event at all for work the
 * first user happened to do, which reads as an undercount nobody can explain.
 */
const reportedKeys = new Set<string>();

/** True the first time a key is seen, false ever after — call it where you would `track`. */
export function reportOnce(key: string): boolean {
  if (reportedKeys.has(key)) return false;
  reportedKeys.add(key);
  return true;
}

/** Drop the identity on sign-out, so the next user of this browser is a new person. */
export function resetUser(): void {
  // Outside `withClient`: this state is ours, and it must be dropped even when PostHog
  // itself is disabled, or a key set while it was on outlives the user who set it.
  reportedKeys.clear();
  withClient((client) => client.reset());
}
